import { useRef, useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { IconButton } from "@mui/material"
import { ChevronLeft, ChevronRight } from "@mui/icons-material"
import "./styles.scss"

/**
 * Scrollable legend component with arrow controls instead of horizontal scrollbars
 * Provides better UX and consistent behavior across browsers
 * Note: If we ever need to support a very large number of LegendItems, we should look into using IntersectionObserver for better performance
 */

const EDGE_THRESHOLD = 5

export default function ScrollableLegend({ children, className = "" }) {
  const scrollContainerRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [showControls, setShowControls] = useState(false)
  const animationFrameRef = useRef(null)
  const isScrollingRef = useRef(false)

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollLeft, scrollWidth, clientWidth } = container
    const hasOverflow = scrollWidth > clientWidth

    setShowControls(hasOverflow)
    setCanScrollLeft(scrollLeft > EDGE_THRESHOLD)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - EDGE_THRESHOLD)
  }, [])

  // Throttled scroll handler using requestAnimationFrame
  const handleScroll = useCallback(() => {
    if (!isScrollingRef.current) {
      isScrollingRef.current = true

      animationFrameRef.current = requestAnimationFrame(() => {
        updateScrollState()
        isScrollingRef.current = false
      })
    }
  }, [updateScrollState])

  // Update scroll state on mount and resize
  useEffect(() => {
    updateScrollState()

    const container = scrollContainerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateScrollState)
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [updateScrollState])

  // Update scroll state when children change using MutationObserver
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const mutationObserver = new MutationObserver(() => {
      requestAnimationFrame(updateScrollState)
    })

    // Observe changes to the legend content
    const legendContent = container.querySelector(".legend-content")
    if (legendContent) {
      mutationObserver.observe(legendContent, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style"], // Watch for style/class changes that might affect layout
      })
    }

    requestAnimationFrame(updateScrollState)

    return () => {
      mutationObserver.disconnect()
    }
  }, [children, updateScrollState])

  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = Math.min(300, container.clientWidth * 0.75)
    container.scrollBy({ left: -scrollAmount, behavior: "smooth" })
  }, [])

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = Math.min(300, container.clientWidth * 0.75)
    container.scrollBy({ left: scrollAmount, behavior: "smooth" })
  }, [])

  return (
    <div className={`scrollable-legend ${className}`}>
      {showControls && canScrollLeft && (
        <div className="scroll-control scroll-control-left">
          <IconButton
            onClick={scrollLeft}
            size="small"
            className="scroll-arrow"
            aria-label="Scroll left"
          >
            <ChevronLeft />
          </IconButton>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="legend-scroll-container"
        onScroll={handleScroll}
      >
        <div className="legend-content">
          {children}
        </div>
      </div>

      {showControls && canScrollRight && (
        <div className="scroll-control scroll-control-right">
          <IconButton
            onClick={scrollRight}
            size="small"
            className="scroll-arrow"
            aria-label="Scroll right"
          >
            <ChevronRight />
          </IconButton>
        </div>
      )}
    </div>
  )
}

ScrollableLegend.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}
